/**
 * Runtime capability registry.
 *
 * Single source of truth for what the current platform supports.
 * UI components should check these flags rather than inlining
 * `window.electron`, `isInTauriShell()`, or UA sniffing.
 *
 * Capabilities are computed once at module load and are immutable for
 * the lifetime of the page (no hot-reload of hardware capabilities).
 */

import { isInTauriShell, isMobilePlatform, getPlatformFallback, isIpadOS } from './detector'

export interface AppCapabilities {
  /**
   * Can print without a dialog. Electron always can; Tauri can too, but on Android
   * only over RAW/JetDirect — the Kotlin bridge skips the dialog exactly when a
   * `printerIp` is supplied, so check {@link silentPrintNeedsIp} before promising it.
   */
  silentPrint: boolean
  /**
   * Can enumerate printers. Electron via IPC, Tauri via `list_printers` — real device
   * names on Tauri desktop, a single "System Print (via Android)" placeholder on Android.
   * Either way the dropdown has something true to show, which is the point.
   */
  printerDiscovery: boolean
  /** Silent print requires an explicit printer IP (Android), rather than a printer name. */
  silentPrintNeedsIp: boolean
  /** Can connect to USB/gphoto2 DSLR cameras (Electron desktop only) */
  usbCamera: boolean
  /** Can save files to local disk / gallery */
  fileDownload: boolean
  /** Can configure app to auto-launch at OS startup (Electron desktop only) */
  autoLaunch: boolean
  /** Has a tunnel URL feature for remote printing (Electron desktop only) */
  tunnelUrl: boolean
  /** Can enter/exit window fullscreen (same kiosk semantics on desktop, phone browser, and Tauri webview). */
  manageFullscreen: boolean
  /**
   * The window is fullscreen for the lifetime of the process and the app cannot change it.
   * True on Android, where MainActivity applies sticky-immersive mode on create AND on every
   * focus regain, `tauri.conf.json` sets `fullscreen: true`, and the Rust `set_fullscreen`
   * command is an explicit no-op under `#[cfg(mobile)]`. Also true for installed home-screen
   * web apps (display-mode: standalone — e.g. iPad "Add to Home Screen"): there is no browser
   * chrome to hide and iOS exposes no working fullscreen API, so the padlock's DOM-fullscreen
   * request silently failed and the kiosk could never be locked. Asking the window what it is,
   * or telling it to change, is meaningless in both cases — so don't: the padlock flips the
   * kiosk-lock flag locally instead (see useFullscreen).
   */
  fullscreenIsPlatformFixed: boolean
  /** Platform supports a physical back button / swipe-back gesture (Android) */
  hardwareBack: boolean
  /**
   * Platform needs safe-area inset CSS padding (iOS status bar / notch, Android
   * gesture bar). On iOS this is only non-zero once the page owns the status-bar
   * strip — `viewport-fit=cover` plus `apple-mobile-web-app-status-bar-style:
   * black-translucent`, both set in index.html — so top chrome must pad by
   * `env(safe-area-inset-top)` or it lands under the clock.
   */
  safeAreaInsets: boolean
  /** Is a mobile platform (phone or tablet) */
  isMobile: boolean
  /** ESC/POS thermal receipt print (Android Tauri bahelastruk) */
  thermalPrint: boolean
  thermalSilent: boolean
  thermalDiscovery: boolean
  /**
   * Can sign in with Google. Electron uses its native `oauth:google` bridge;
   * Tauri uses the system-browser handoff in lib/auth/googleOAuthHandoff.
   * Both need a real system browser to hand off to — Google refuses embedded
   * webviews — so a plain browser tab is excluded: it has the web dashboard's
   * own Google button already.
   */
  googleOAuth: boolean
}

/**
 * Derived from the shell + user agent ONLY — deliberately never from the Tauri
 * `get_runtime_info` command.
 *
 * The renderer is web-served and reaches every booth on the next reload, but APKs
 * are not: v2.3.1 is in the field reporting `fullscreenControl: true` and
 * `silentPrint: false` for Android, both of which are wrong. Sourcing these flags
 * from the shell would make this file only as correct as the oldest installed APK,
 * and would undo the fixes here on exactly the devices that need them.
 *
 * UA + `__TAURI__` are stable across every build ever shipped, so they stay the input.
 */
function computeCapabilities(): AppCapabilities {
  const isElectron = typeof window !== 'undefined' && typeof window.electron !== 'undefined'
  const isTauri = isInTauriShell()
  const isMobile = isMobilePlatform()
  const platform = getPlatformFallback()
  const isAndroidTauri = isTauri && platform === 'android'
  // Installed home-screen web app (iOS "Add to Home Screen" / Android PWA install).
  // `navigator.standalone` is the legacy iOS signal; display-mode covers the rest.
  const isStandaloneWebApp =
    !isElectron &&
    !isTauri &&
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true)
  const fullscreenIsPlatformFixed = isAndroidTauri || isStandaloneWebApp

  return {
    // Tauri has a native `print_job`/`list_printers` pair, so neither of these is
    // Electron-only. Reporting them as Electron-only is what made the UI hide the
    // printers Tauri had already found.
    silentPrint: isElectron || isTauri,
    printerDiscovery: isElectron || isTauri,
    silentPrintNeedsIp: isAndroidTauri,
    usbCamera: isElectron,
    fileDownload: true,
    autoLaunch: isElectron,
    tunnelUrl: isElectron,
    manageFullscreen: !fullscreenIsPlatformFixed,
    fullscreenIsPlatformFixed,
    hardwareBack: platform === 'android',
    // iPad is the booth device this matters most for, and it is exactly the one
    // `isMobile` misses: iPadOS 13+ sends a desktop Mac UA. See isIpadOS().
    safeAreaInsets: isMobile || isTauri || isIpadOS(),
    isMobile,
    thermalPrint: isAndroidTauri,
    thermalSilent: isAndroidTauri,
    thermalDiscovery: isAndroidTauri,
    googleOAuth: isElectron || isTauri,
  }
}

// Singleton — evaluated once at module import time.
export const capabilities: AppCapabilities = computeCapabilities()
